"use client";

import { create } from "zustand";
import type { ActionReveal } from "@/lib/api";

/**
 * U24 — Chat reveal navigation store.
 *
 * Bridges the typed `ActionReveal` contract from U20 (`{surface, id, label}`)
 * into the page-level tab/sub-tab router. ChatScreen calls `request()` when
 * an `executeAction` response carries a reveal; `page.tsx` subscribes to
 * `pending`, routes the user to the correct surface, and calls `consume()`
 * so the navigation only fires once.
 */

interface ChatRevealState {
  pending: ActionReveal | null;
  active: ActionReveal | null;
  /** Set by ChatScreen after a successful action that returned a reveal. */
  request: (reveal: ActionReveal) => void;
  /** Cleared by the router after navigation fires; active remains for highlighting. */
  consume: () => void;
  /** Cleared by the target surface after it finds and pulses the real row. */
  clearActive: (id?: string) => void;
}

export const useChatRevealStore = create<ChatRevealState>((set) => ({
  pending: null,
  active: null,
  request: (reveal) => set({ pending: reveal, active: reveal }),
  consume: () => set({ pending: null }),
  clearActive: (id) =>
    set((state) => {
      if (id && state.active?.id !== id) return state;
      return { active: null };
    }),
}));

/** Sub-tab identifier used by `page.tsx` for the Decide tab views. */
export type DecideView = "decisions" | "library" | "goals" | "balance";

/** What `page.tsx` needs to flip to land the user on the reveal target. */
export interface RevealRoute {
  /** Top-level tab id used by `setActiveTab` (`present|schedule|decide|life`). */
  activeTab: "present" | "schedule" | "decide" | "life";
  /** Sub-view inside Decide (only meaningful when `activeTab === "decide"`). */
  decideView: DecideView | null;
}

const DECIDE_VIEW_BY_SURFACE: Record<string, DecideView> = {
  goals: "goals",
  library: "library",
  decisions: "decisions",
  balance: "balance",
};

/**
 * Pure mapping from backend `ActionReveal.surface` to the
 * `(activeTab, decideView)` pair `page.tsx` switches between.
 *
 * Unknown surfaces fall back to Today so the user never lands on a
 * blank screen — this is a navigation contract, not validation.
 */
export function surfaceToTab(
  surface: ActionReveal["surface"] | (string & {})
): RevealRoute {
  switch (surface) {
    case "plan":
      return { activeTab: "schedule", decideView: null };
    case "reflect":
      return { activeTab: "life", decideView: null };
    case "goals":
    case "library":
    case "decisions":
    case "balance":
      return {
        activeTab: "decide",
        decideView: DECIDE_VIEW_BY_SURFACE[surface] ?? "decisions",
      };
    case "today":
    default:
      return { activeTab: "present", decideView: null };
  }
}

/** Human-readable label for the destination surface, used by the banner. */
export function surfaceLabel(surface: ActionReveal["surface"] | string): string {
  switch (surface) {
    case "today":
      return "Today";
    case "plan":
      return "Plan";
    case "reflect":
      return "Reflect";
    case "goals":
      return "Goals";
    case "library":
      return "Library";
    case "decisions":
      return "Decisions";
    case "balance":
      return "Balance";
    default:
      return "your workspace";
  }
}
